import { observer } from 'mobx-react-lite'
import {
  Box, Card, CardContent, Typography, Stack
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Shield as RolesIcon,
  ManageAccounts as UsersAdminIcon,
  ChevronRight as ChevronRightIcon,
  Label as TagsIcon,
  AccountTree as TenantIcon,
  ViewKanban as PipelineIcon,
  AttachFile as EmailFilesIcon,
  AccessTime as ExpiryIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { PageHeader } from '../components/shared/PageHeader'
import { themeColors } from '../app/layout/theme'
import { usePermission } from '../lib/hooks/usePermission'

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
      <Box sx={{ color: themeColors.primary, mt: '2px' }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: themeColors.text.primary }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary, mt: '2px' }}>{subtitle}</Typography>}
      </Box>
    </Box>
  )
}

// ── Settings Option Card Row ──────────────────────────────────────────────────
interface SettingOptionProps {
  icon: React.ReactNode;
  bgColor: string;
  title: string;
  description: string;
  onClick: () => void;
}

function SettingOption({ icon, bgColor, title, description, onClick }: SettingOptionProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        borderRadius: '10px',
        border: `1px solid ${themeColors.border}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          bgcolor: '#f8fafc',
          borderColor: themeColors.primary,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transform: 'translateY(-2px)'
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: themeColors.text.primary }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary, mt: '2px' }}>
          {description}
        </Typography>
      </Box>
      <ChevronRightIcon sx={{ color: themeColors.text.muted }} />
    </Box>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const SettingsPage = observer(() => {
  const navigate = useNavigate()
  const { canViewMenu, hasAction, isSuperAdmin } = usePermission()

  const canSeeTags    = canViewMenu('/settings')   // true for Admin (LobId empty) + SuperAdmin
  const canSeeTenants = isSuperAdmin            // only real SuperAdmin sees Tenant Settings
  const canSeeKnowlarity = canViewMenu('/settings/knowlarity')

  const cardSx = {
    borderRadius: '14px',
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.shadow.sm,
  }

  return (
    <Box>
      <PageHeader
        title="Settings"
        breadcrumb="Account"
      />

      <Box sx={{ px: 3, pb: 4, maxWidth: 820 }}>
        <Stack spacing={3}>
          <Card sx={cardSx}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeading
                icon={<SettingsIcon />}
                title="System Settings"
                subtitle="Configure settings, users, roles, tags, stages and assets across your system"
              />
              <Stack spacing={1.5}>
                {/* Tags */}
                {canSeeTags && (
                  <SettingOption
                    icon={<TagsIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                    bgColor="#f2eef9"
                    title="Tags"
                    description="Create and manage tags used to categorise leads"
                    onClick={() => navigate('/settings/tag')}
                  />
                )}

                {/* Deal Stages */}
                <SettingOption
                  icon={<PipelineIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                  bgColor="#f2eef9"
                  title="Deal Stages"
                  description="Configure the stages in your sales pipeline — names, colours, and win probabilities"
                  onClick={() => navigate('/settings/pipeline-stages')}
                />

                {/* Sales Expiry Settings */}
                <SettingOption
                  icon={<ExpiryIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                  bgColor="#f2eef9"
                  title="Sales Expiry Settings"
                  description="Configure automatic expiry duration (days) for newly created Sales"
                  onClick={() => navigate('/settings/sales-expiry')}
                />

                {/* Email Files */}
                <SettingOption
                  icon={<EmailFilesIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                  bgColor="#f2eef9"
                  title="Email Files"
                  description="Upload PDFs, images, and documents to use as quick attachments when sending emails"
                  onClick={() => navigate('/settings/email-files')}
                />

                {/* Knowlarity Settings */}
                {canSeeKnowlarity && (
                  <SettingOption
                    icon={<SettingsIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                    bgColor="#f2eef9"
                    title="Knowlarity Settings"
                    description="Configure K-Number and Caller ID virtual numbers for click-to-dial integration"
                    onClick={() => navigate('/settings/knowlarity')}
                  />
                )}

                {/* Tenant Settings */}
                {canSeeTenants && (
                  <SettingOption
                    icon={<TenantIcon sx={{ fontSize: 22, color: themeColors.primary }} />}
                    bgColor="#f2eef9"
                    title="Tenant Settings"
                    description="Manage organisations, lines of business, and warehouses"
                    onClick={() => navigate('/admin/tenants')}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Box>
  )
})

export default SettingsPage
